// Single-stroke (monoline) vector font for the TypeShapes tool.
// Uppercase display font on a fixed grid; y increases downward.
// Cap line at y=0, baseline at y=14. No descenders.

export interface Glyph {
  /** One or more pen strokes; each stroke is a polyline of [x, y] points. */
  strokes: ReadonlyArray<ReadonlyArray<readonly [number, number]>>;
  /** Advance width in grid units (where to place the next glyph's origin). */
  width: number;
}

/** baseline-to-cap height in grid units */
export const FONT_CAP = 14;
/** recommended extra spacing between glyphs */
export const FONT_GAP = 4;

export const STROKE_FONT: Record<string, Glyph> = {
  ' ': { strokes: [], width: 8 },

  // ---- Letters ----
  A: {
    strokes: [
      [[0, 14], [5, 0], [10, 14]],
      [[2, 8], [8, 8]],
    ],
    width: 12,
  },
  B: {
    strokes: [
      [[0, 0], [0, 14]],
      [
        [0, 0], [6, 0], [8, 1], [9, 3], [8, 6], [6, 7], [0, 7],
      ],
      [
        [0, 7], [7, 7], [9, 8], [10, 10], [9, 13], [7, 14], [0, 14],
      ],
    ],
    width: 12,
  },
  C: {
    strokes: [
      [
        [10, 3], [8, 1], [5, 0], [3, 1], [1, 3], [0, 7],
        [1, 11], [3, 13], [5, 14], [8, 13], [10, 11],
      ],
    ],
    width: 12,
  },
  D: {
    strokes: [
      [[0, 0], [0, 14]],
      [
        [0, 0], [5, 0], [8, 2], [10, 5], [10, 9], [8, 12], [5, 14], [0, 14],
      ],
    ],
    width: 12,
  },
  E: {
    strokes: [
      [[0, 0], [0, 14]],
      [[0, 0], [10, 0]],
      [[0, 7], [8, 7]],
      [[0, 14], [10, 14]],
    ],
    width: 12,
  },
  F: {
    strokes: [
      [[0, 0], [0, 14]],
      [[0, 0], [10, 0]],
      [[0, 7], [8, 7]],
    ],
    width: 12,
  },
  G: {
    strokes: [
      [
        [10, 3], [8, 1], [5, 0], [3, 1], [1, 3], [0, 7],
        [1, 11], [3, 13], [5, 14], [8, 13], [10, 11], [10, 8],
      ],
      [[6, 8], [10, 8]],
    ],
    width: 12,
  },
  H: {
    strokes: [
      [[0, 0], [0, 14]],
      [[10, 0], [10, 14]],
      [[0, 7], [10, 7]],
    ],
    width: 12,
  },
  I: {
    strokes: [
      [[0, 0], [4, 0]],
      [[2, 0], [2, 14]],
      [[0, 14], [4, 14]],
    ],
    width: 6,
  },
  J: {
    strokes: [
      [[8, 0], [8, 10], [7, 13], [4, 14], [2, 13], [1, 11], [1, 9]],
    ],
    width: 11,
  },
  K: {
    strokes: [
      [[0, 0], [0, 14]],
      [[10, 0], [0, 8]],
      [[3, 6], [10, 14]],
    ],
    width: 12,
  },
  L: {
    strokes: [
      [[0, 0], [0, 14], [10, 14]],
    ],
    width: 12,
  },
  M: {
    strokes: [
      [[0, 14], [0, 0], [6, 9], [12, 0], [12, 14]],
    ],
    width: 14,
  },
  N: {
    strokes: [
      [[0, 14], [0, 0], [10, 14], [10, 0]],
    ],
    width: 12,
  },
  O: {
    strokes: [
      [
        [5, 0], [3, 1], [1, 3], [0, 7], [1, 11], [3, 13],
        [5, 14], [7, 13], [9, 11], [10, 7], [9, 3], [7, 1], [5, 0],
      ],
    ],
    width: 12,
  },
  P: {
    strokes: [
      [[0, 0], [0, 14]],
      [
        [0, 0], [6, 0], [8, 1], [9, 3], [8, 6], [6, 7], [0, 7],
      ],
    ],
    width: 12,
  },
  Q: {
    strokes: [
      [
        [5, 0], [3, 1], [1, 3], [0, 7], [1, 11], [3, 13],
        [5, 14], [7, 13], [9, 11], [10, 7], [9, 3], [7, 1], [5, 0],
      ],
      [[6, 10], [11, 15]],
    ],
    width: 12,
  },
  R: {
    strokes: [
      [[0, 0], [0, 14]],
      [
        [0, 0], [6, 0], [8, 1], [9, 3], [8, 6], [6, 7], [0, 7],
      ],
      [[5, 7], [10, 14]],
    ],
    width: 12,
  },
  S: {
    strokes: [
      [
        [10, 3], [8, 1], [5, 0], [2, 1], [1, 3], [2, 5], [5, 6],
        [8, 8], [9, 10], [8, 13], [5, 14], [2, 13], [0, 11],
      ],
    ],
    width: 12,
  },
  T: {
    strokes: [
      [[0, 0], [10, 0]],
      [[5, 0], [5, 14]],
    ],
    width: 12,
  },
  U: {
    strokes: [
      [
        [0, 0], [0, 10], [1, 13], [3, 14], [7, 14], [9, 13], [10, 10], [10, 0],
      ],
    ],
    width: 12,
  },
  V: {
    strokes: [
      [[0, 0], [5, 14], [10, 0]],
    ],
    width: 12,
  },
  W: {
    strokes: [
      [[0, 0], [3, 14], [6, 5], [9, 14], [12, 0]],
    ],
    width: 14,
  },
  X: {
    strokes: [
      [[0, 0], [10, 14]],
      [[10, 0], [0, 14]],
    ],
    width: 12,
  },
  Y: {
    strokes: [
      [[0, 0], [5, 7], [10, 0]],
      [[5, 7], [5, 14]],
    ],
    width: 12,
  },
  Z: {
    strokes: [
      [[0, 0], [10, 0], [0, 14], [10, 14]],
    ],
    width: 12,
  },

  // ---- Digits ----
  '0': {
    strokes: [
      [
        [4, 0], [2, 1], [1, 3], [0, 7], [1, 11], [2, 13],
        [4, 14], [6, 13], [7, 11], [8, 7], [7, 3], [6, 1], [4, 0],
      ],
      [[7, 2], [1, 12]],
    ],
    width: 10,
  },
  '1': {
    strokes: [
      [[1, 3], [4, 0], [4, 14]],
      [[1, 14], [7, 14]],
    ],
    width: 10,
  },
  '2': {
    strokes: [
      [
        [0, 3], [1, 1], [4, 0], [7, 1], [8, 3], [7, 6],
        [0, 14], [8, 14],
      ],
    ],
    width: 10,
  },
  '3': {
    strokes: [
      [[0, 1], [5, 0], [8, 2], [7, 5], [4, 7]],
      [[4, 7], [7, 8], [8, 11], [5, 14], [1, 13], [0, 11]],
    ],
    width: 10,
  },
  '4': {
    strokes: [
      [[6, 0], [0, 10], [8, 10]],
      [[6, 0], [6, 14]],
    ],
    width: 10,
  },
  '5': {
    strokes: [
      [[7, 0], [1, 0], [0, 6], [4, 5], [7, 7], [8, 10], [6, 13], [3, 14], [0, 12]],
    ],
    width: 10,
  },
  '6': {
    strokes: [
      [[7, 1], [4, 0], [2, 1], [0, 5], [0, 10], [1, 13], [4, 14], [6, 13], [7, 10], [6, 8], [3, 7], [1, 8], [0, 10]],
    ],
    width: 10,
  },
  '7': {
    strokes: [
      [[0, 0], [8, 0], [3, 14]],
    ],
    width: 10,
  },
  '8': {
    strokes: [
      [
        [4, 7], [2, 6], [1, 4], [2, 1], [4, 0], [6, 1], [7, 4], [6, 6], [4, 7],
        [2, 8], [0, 10], [1, 13], [4, 14], [7, 13], [8, 10], [6, 8], [4, 7],
      ],
    ],
    width: 10,
  },
  '9': {
    strokes: [
      [[1, 13], [4, 14], [6, 13], [8, 9], [8, 4], [7, 1], [4, 0], [2, 1], [1, 4], [2, 6], [5, 7], [7, 6], [8, 4]],
    ],
    width: 10,
  },

  // ---- Punctuation ----
  '.': {
    strokes: [
      [[2, 13], [3, 13], [3, 14], [2, 14], [2, 13]],
    ],
    width: 6,
  },
  ',': {
    strokes: [
      [[3, 13], [3, 14], [1, 17]],
    ],
    width: 6,
  },
  '!': {
    strokes: [
      [[2, 0], [2, 10]],
      [[2, 13], [2, 14]],
    ],
    width: 6,
  },
  '?': {
    strokes: [
      [[0, 3], [1, 1], [4, 0], [7, 1], [8, 3], [7, 6], [4, 8], [4, 10]],
      [[4, 13], [4, 14]],
    ],
    width: 10,
  },
  '-': {
    strokes: [
      [[1, 7], [7, 7]],
    ],
    width: 9,
  },
  "'": {
    strokes: [
      [[2, 0], [2, 4]],
    ],
    width: 5,
  },
  ':': {
    strokes: [
      [[2, 4], [2, 5]],
      [[2, 13], [2, 14]],
    ],
    width: 5,
  },
  '(': {
    strokes: [
      [[5, 0], [2, 4], [1, 7], [2, 10], [5, 14]],
    ],
    width: 7,
  },
  ')': {
    strokes: [
      [[1, 0], [4, 4], [5, 7], [4, 10], [1, 14]],
    ],
    width: 7,
  },
  '/': {
    strokes: [
      [[0, 14], [8, 0]],
    ],
    width: 9,
  },
};

/** Look up a glyph for a character; letters are matched case-insensitively (uppercase forms). */
export function glyphFor(ch: string): Glyph | undefined {
  const g = STROKE_FONT[ch] ?? STROKE_FONT[ch.toUpperCase()];
  return g;
}
